import {
  getMarketingConsent,
  getUserSpecificConsent,
} from "@/services/consent.service";
import { useCallback, useEffect, useRef } from "react";
import { CheckboxFieldConfig } from "@/models/formField";
import {
  MARKETING_CONSENT_MODAL_ID,
  MARKETING_CONSENT_STATUS,
} from "@/constants/general";
import { ModalType } from "@/models/common";
import { useModal } from "@/hooks/useModal";
import useAuth from "@/hooks/useAuth";
import {
  buildEmailidConfig,
  conditionallyDisplayMarketingModal,
  consentCheckboxConfigFormatter,
  deleteMarketingConsentCookie,
  getMarketingConsentInfoFromCookie,
} from "@/helpers/marketingConsent.helper";
import { useCountry } from "@/hooks/useCountry";
import { MarketingConsentLayoutProps } from "@/models/marketingConsent";
import { useMarketingConsentStore } from "@/stores/marketingConsentStore";
import styles from "./MarketingConsentLayout.module.scss";
import { MarketingConsentPopup } from "@/components/MarketingConsentPopup";
import { useLocation } from "react-router-dom";
import { routes } from "@/constants/routes";

// Helper function to calculate modal width based on screen size
const getModalWidth = (): string => {
  if (typeof window === "undefined") return "95vw";
  const width = window.innerWidth;
  if (width >= 1200) return "50vw"; // XL screens
  if (width >= 576) return "70vw"; // SM to XL screens
  return "95vw"; // Mobile screens - use 95vw instead of 100vw to prevent full page
};

export const MarketingConsentLayout: React.FC<MarketingConsentLayoutProps> = ({
  children,
}) => {
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const { country } = useCountry();
  const { showModal, closeModal } = useModal();
  const auth: any = useAuth();
  const isLoggedIn: boolean = auth?.isAuthenticated;
  const { hasMarketingConsentShown, setHasMarketingConsentShown } =
    useMarketingConsentStore();
  const location = useLocation();

  const emailIdConfig = buildEmailidConfig(styles);

  const hideModal = useCallback(() => {
    closeModal(MARKETING_CONSENT_MODAL_ID);
    // Set that marketing consent popup has been shown to prevent re-showing on page navigations and unnecessary API calls
    setHasMarketingConsentShown(true);
  }, [closeModal, setHasMarketingConsentShown]);

  // Function to display the marketing consent modal
  const displayMarketingConsentModal = useCallback(
    (checkboxConfig: CheckboxFieldConfig | null) => {
      // Show modal
      showModal({
        uId: MARKETING_CONSENT_MODAL_ID,
        children: (
          <MarketingConsentPopup
            checkboxConfig={checkboxConfig}
            emailIdConfig={emailIdConfig}
            hideModal={hideModal}
          />
        ),
        type: ModalType.CUSTOM,
        showFooter: false,
        closable: false,
        className: styles.marketing_popup_container,
        width: getModalWidth(),
        preventModalStacking: true,
      });
    },
    [showModal, emailIdConfig, hideModal],
  );

  // To fetch data from API/cookie and show modal accordingly
  const getMarketingConsentData = async () => {
    let checkboxConfig: CheckboxFieldConfig | null = null;
    let isConsentGiven = false;
    // Check cookie for marketing consent
    const consentCookieData = getMarketingConsentInfoFromCookie();
    if (isLoggedIn) {
      // Prevent modal being shown on other pages to logged in users; Show modal only in home page
      if (location.pathname !== routes.home.path) {
        return;
      }
      // For logged in users, fetch consent data from backend
      try {
        const [marketingConsentResponse, allConsentResponse] =
          await Promise.all([getMarketingConsent(), getUserSpecificConsent()]);
        const marketingResponseData = marketingConsentResponse?.data?.data;
        if (marketingResponseData) {
          const config: any = marketingResponseData?.[0];
          checkboxConfig = config
            ? consentCheckboxConfigFormatter(config, styles.checkbox_consent)
            : null;

          // Check if logged in user has already given consent
          const consentFromApi = allConsentResponse?.data?.data?.some(
            (item: any) =>
              checkboxConfig &&
              item.consentId === checkboxConfig?.name &&
              (item.status === MARKETING_CONSENT_STATUS.ACTIVE ||
                item.status === MARKETING_CONSENT_STATUS.WITHDRAWN),
          );

          isConsentGiven = consentFromApi;
          // No need to show modal since modal was closed earlier
          if (
            !isConsentGiven &&
            consentCookieData &&
            consentCookieData.isLoggedInUser &&
            !consentCookieData.consent
          ) {
            isConsentGiven = true;
          }

          // Delete the cookie since consent is coming from API or if the cookie was set by anonymous user
          if (
            consentFromApi ||
            (consentCookieData && !consentCookieData.isLoggedInUser)
          ) {
            deleteMarketingConsentCookie();
          }
        }
      } catch (error) {
        console.error("Error fetching marketing consent data:", error);
        // Stop running to prevent modal being shown since an error occured
        return;
      }
    } else {
      //  No need to show modal if consent cookie exists
      if (consentCookieData && !consentCookieData.isLoggedInUser) {
        isConsentGiven = true;
      }

      // Delete the cookie if the cookie was set by logged in user
      if (consentCookieData?.isLoggedInUser) {
        deleteMarketingConsentCookie();
      }
    }
    //To show the modal
    conditionallyDisplayMarketingModal({
      isConsentGiven,
      timeoutId,
      isLoggedIn,
      displayMarketingConsentModal,
      checkboxConfig,
    });

    // Update atom state to prevent further API calls since consent is already given
    if (isConsentGiven) {
      setHasMarketingConsentShown(true);
    }
  };

  // Handle marketing consent popup visibility only after country popup is shown and country is selected
  useEffect(() => {
    if (!hasMarketingConsentShown && country) {
      getMarketingConsentData();
    }
  }, [country, hasMarketingConsentShown]);

  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  return children;
};
